import { Component, OnInit, Inject, PLATFORM_ID, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { Herb } from '../../../models/herb.model';
import { HERBS } from '../../../data/herbs';
import { RouterModule } from '@angular/router';
import { ImageService } from '../../../services/image.service';
import { HttpClient } from '@angular/common/http';
import { BlogService } from '../../../services/blog.service';
import { BlogPost } from '../../../models/blog-post.models';
import { LoadingSpinnerComponent } from '../../../ui/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-hero',
  standalone: true,
  imports: [CommonModule, RouterModule, LoadingSpinnerComponent, NgOptimizedImage],
  templateUrl: './hero.component.html',
  styleUrls: ['./hero.component.css'],
  providers: [DatePipe],
})
export class HeroComponent implements OnInit {

  containerWidth = 800; // Standardwert
  containerHeight = 600; // Standardwert
  aspectRatio = 0.75; // Standardwert (3:4)
  dpr: number = 1; // Neue Variable für Device Pixel Ratio

  loading: boolean = false;
  // Monatsnamen für den Kalender
  months: string[] = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
  
  // Aktuelles Datum - hier kannst du ein Testdatum setzen
  currentDate: Date = new Date();
  // currentDate: Date =  new Date(2025, 3, 1) // Für Tests

  // Aktueller Monat (0-basiert), abgeleitet vom aktuellen Datum
  currentMonthIndex: number = this.currentDate.getMonth();

  isBrowser: boolean;
  isLoading: boolean = true;
  
  // Kraut des Tages
  herbOfTheDay: Herb;

  // Empfohlene Artikel
  recommendedArticles: BlogPost[] = [];

  constructor(
    public imageService: ImageService,
    private blogService: BlogService,
    private cdRef: ChangeDetectorRef, // ChangeDetectorRef hinzufügen
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    
    // Kraut des Tages initialisieren
    this.herbOfTheDay = this.initHerbOfTheDay();
    
    // Empfohlene Artikel initialisieren
    this.initRecommendedArticles();
    
    // Debug-Ausgaben
    // console.log(`Datum: ${this.currentDate.toLocaleDateString()}`);
    // console.log(`Tag des Jahres: ${this.getDayOfYear(this.currentDate)}`);
    // console.log(`Monat: ${this.months[this.currentMonthIndex]}`);
    // console.log(`Gewähltes Kraut: ${this.herbOfTheDay.name}`);
  }

  @ViewChild('imageContainer', {static: false}) imageContainer?: ElementRef;


  ngOnInit(): void {
  
  }

ngAfterViewInit() {
  if(this.isBrowser) {
    this.dpr = window.devicePixelRatio || 1;
    
    if(this.imageContainer) {
      this.containerWidth = this.imageContainer.nativeElement.offsetWidth;
      this.containerHeight = Math.round(this.containerWidth * this.aspectRatio);
      // Markiere die View als zu prüfen, ohne sofort zu aktualisieren
      this.cdRef.detectChanges();
    }
    
    window.addEventListener('resize', this.onResize.bind(this));
  }
}
  onResize() {
    if(this.imageContainer) {
      this.containerWidth = this.imageContainer.nativeElement.offsetWidth;
      this.containerHeight = Math.round(this.containerWidth * this.aspectRatio);
    }
  }

  ngOnDestroy() {
    if(this.isBrowser) {
      // Immer Eventlistener entfernen, wenn die Komponente zerstört wird
      window.removeEventListener('resize', this.onResize);
    }
  }


  // Hilfsfunktion zur Berechnung des Tages im Jahr (1-366)
  private getDayOfYear(date: Date): number {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date.getTime() - start.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
  }

  advanceToNextDay(): void {
    const nextDay = new Date(this.currentDate);
    nextDay.setDate(nextDay.getDate() + 1);
    this.currentDate = nextDay;
    this.currentMonthIndex = this.currentDate.getMonth();
    this.herbOfTheDay = this.initHerbOfTheDay();
    this.initRecommendedArticles();
  }


  private initHerbOfTheDay(): Herb {
    // Berechne einen deterministischen Seed basierend auf dem gesetzten Datum
    const dayOfYear = this.getDayOfYear(this.currentDate);
    const year = this.currentDate.getFullYear();
    const seedValue = dayOfYear + year;
    
    // Filtere Kräuter nach Saison
    const herbsInSeason = HERBS.filter(herb => this.isHarvestable(herb, this.currentMonthIndex));
    const peakHerbs = herbsInSeason.filter(herb => this.isInPeakSeason(herb));
    
    // Wähle die zu verwendende Liste - priorisiere Hauptsaison, dann Saison, dann alle
    let herbList = HERBS;
    if (peakHerbs.length > 0) {
      herbList = peakHerbs;
      // console.log(`${peakHerbs.length} Kräuter in der Hauptsaison gefunden.`);
    } else if (herbsInSeason.length > 0) {
      herbList = herbsInSeason;
      // console.log(`${herbsInSeason.length} Kräuter in der Erntezeit gefunden.`);
    } else {
      // console.log(`Keine Kräuter in Saison, verwende alle ${HERBS.length} Kräuter.`);
    }
    
    // Wähle deterministisch ein Kraut aus der Liste
    const herbIndex = seedValue % herbList.length;
    // console.log(`Seed: ${seedValue}, Index: ${herbIndex}, Anzahl Kräuter: ${herbList.length}`);
    
    return herbList[herbIndex];
  }

  private initRecommendedArticles(): void {
    if (!this.herbOfTheDay) return;
    
    this.loading = true; // Falls du einen Ladeindikator anzeigen möchtest
    
    this.blogService.getRandomPosts(this.herbOfTheDay.name).subscribe({
      next: (response) => {
        this.recommendedArticles = response.posts;
        this.loading = false;
      },
      error: (error) => {
        console.error('Fehler beim Laden der empfohlenen Artikel:', error);
        this.loading = false;
      }
    });
  }
  // Überprüft, ob ein Kraut im gegebenen Monat (Index 0-11) erntbar ist
  isHarvestable(herb: Herb, monthIndex: number): boolean {
    return herb?.harvestMonths?.includes(monthIndex + 1) || false;
  }

  // Überprüft, ob ein Kraut im aktuellen Monat in der Hauptsaison ist
  isInPeakSeason(herb: Herb): boolean {
    return herb?.peakMonths?.includes(this.currentMonthIndex + 1) || false;
  }
  
  // Überprüft, ob ein Kraut im gegebenen Monat (Index 0-11) in der Hauptsaison ist
  isPeakSeason(herb: Herb, monthIndex: number): boolean {
    return herb?.peakMonths?.includes(monthIndex + 1) || false;
  }
  
  // Prüft, ob der gegebene Monat der aktuelle Monat ist
  isCurrentMonth(monthIndex: number): boolean {
    return monthIndex === this.currentMonthIndex;
  }

  // Gibt eine gekürzte Version der Verwendungstexte zurück
  getShortUsesText(herb: Herb): string {
    if (!herb) return '';
    
    if (typeof herb.uses === 'string') {
      return herb.uses.length > 100 ? herb.uses.substring(0, 100) + '...' : herb.uses;
    } else if (Array.isArray(herb.uses)) {
      const usesText = herb.uses.join(', ');
      return usesText.length > 100 ? usesText.substring(0, 100) + '...' : usesText;
    }
    return 'Keine Angaben zur Verwendung.';
  }
}